<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

/* global axios */

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['migration-completed']);

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const status = ref(null);
const dismissed = ref(false);
let pollInterval = null;

const dismissedKey = computed(
  () => `migration_banner_dismissed_${accountId.value}_${props.inboxId}`
);

const hasStatus = computed(() => {
  if (dismissed.value) return false;
  return status.value && status.value.status !== 'not_found';
});

const statusClasses = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return 'bg-blue-50 border-blue-200';
    case 'completed':
      return 'bg-green-50 border-green-200';
    case 'failed':
      return 'bg-red-50 border-red-200';
    default:
      return 'bg-n-slate-2 border-n-slate-6';
  }
});

const statusIcon = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return '🔄';
    case 'completed':
      return '✅';
    case 'failed':
      return '❌';
    default:
      return 'ℹ️';
  }
});

const statusTitle = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return t('MIGRATE_CONVERSATIONS.STATUS.RUNNING');
    case 'completed':
      return t('MIGRATE_CONVERSATIONS.STATUS.COMPLETED');
    case 'failed':
      return t('MIGRATE_CONVERSATIONS.STATUS.FAILED');
    default:
      return '';
  }
});

const statusMessage = computed(() => {
  return status.value?.message || '';
});

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
};

const fetchStatus = async () => {
  // Don't fetch if inbox not ready
  if (!props.inboxId) {
    return;
  }

  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inboxId}/migrate_conversations/status`
    );

    status.value = response.data;

    // Stop polling if migration is completed or failed
    if (
      status.value.status === 'completed' ||
      status.value.status === 'failed'
    ) {
      stopPolling();
      if (status.value.status === 'completed') {
        emit('migration-completed');
      }
    }
  } catch (error) {
    // 404 means no migration in progress - stop polling
    if (error.response?.status === 404) {
      status.value = null;
      stopPolling();
      return;
    }
    // Log other errors silently
    if (error.response?.status !== 404) {
      // eslint-disable-next-line no-console
      console.warn('[MIGRATE-STATUS] Error fetching status:', error.message);
    }
  }
};

const startPolling = () => {
  // Poll every 3 seconds
  pollInterval = setInterval(fetchStatus, 3000);
};

const dismiss = () => {
  dismissed.value = true;
  localStorage.setItem(dismissedKey.value, 'true');
  status.value = null;
};

const formatTime = timestamp => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleString();
};

onMounted(() => {
  // Check if banner was dismissed
  const wasDismissed = localStorage.getItem(dismissedKey.value);
  if (wasDismissed === 'true') {
    dismissed.value = true;
    return;
  }
  
  fetchStatus();
  startPolling();
});

// Watch for inboxId changes to restart polling
watch(
  () => props.inboxId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      stopPolling();
      status.value = null;
      
      // Check if new inbox banner was dismissed
      const wasDismissed = localStorage.getItem(dismissedKey.value);
      if (wasDismissed === 'true') {
        dismissed.value = true;
        return;
      }
      
      dismissed.value = false;
      fetchStatus();
      startPolling();
    }
  }
);

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div
    v-if="hasStatus"
    class="mb-4 rounded-lg border p-4"
    :class="statusClasses"
  >
    <div class="flex items-start justify-between">
      <div class="flex-1">
        <div class="flex items-center gap-2">
          <span class="text-lg">{{ statusIcon }}</span>
          <h3 class="font-semibold">
            {{ statusTitle }}
          </h3>
        </div>

        <p class="mt-1 text-sm">
          {{ statusMessage }}
        </p>

        <div v-if="status.status === 'running'" class="mt-3">
          <div class="flex items-center justify-between text-xs mb-1">
            <span>
              {{ status.message }}
            </span>
            <span>{{ status.progress || 0 }}%</span>
          </div>
          <div class="h-2 bg-n-slate-3 rounded-full overflow-hidden">
            <div
              class="h-full bg-n-brand transition-all duration-300"
              :style="{ width: `${status.progress || 0}%` }"
            />
          </div>
        </div>

        <p v-if="status.updated_at" class="mt-2 text-xs text-n-slate-10">
          {{ $t('MIGRATE_CONVERSATIONS.STATUS.UPDATED') }}:
          {{ formatTime(status.updated_at) }}
        </p>
      </div>

      <button
        v-if="status.status !== 'running'"
        class="text-n-slate-10 hover:text-n-slate-12 ml-4"
        @click="dismiss"
      >
        ✕
      </button>
    </div>
  </div>
</template>
